/**
 * PLATFORM_SPECS — the single source of truth for per-platform limits, aspect
 * ratios, capabilities and brand metadata. Used by the composer (char counters,
 * previews, renditions), the connectors (payload validation) and the UI chips.
 *
 * Numbers reflect the real public API/product limits at time of writing.
 */

import type { PlatformId, ValidationIssue } from './types';
import { PLATFORM_IDS } from './types';

export interface AspectRatio {
  /** Human label used in the rendition UI, e.g. "Square 1:1". */
  label: string;
  /** Stable id used as `media_renditions.label`. */
  id: string;
  /** width / height */
  ratio: number;
  /** Recommended pixel dimensions for export. */
  width: number;
  height: number;
  /** The default rendition offered for this platform. */
  recommended?: boolean;
}

export interface PlatformLimits {
  /** Max characters in the main body/caption. See `unlimitedChars`. */
  maxChars: number;
  /** True when the platform has no practical body limit (WordPress). */
  unlimitedChars: boolean;
  /** Soft target the composer nudges toward (engagement sweet spot). */
  recommendedChars: number;
  maxHashtags: number;
  /** Hashtags the platform's culture actually rewards. */
  recommendedHashtags: number;
  maxImages: number;
  /**
   * How many images this app's connector actually publishes today. Equal to
   * `maxImages` where we support the platform's full album/carousel, lower
   * where the connector still posts a single image (Facebook `/photos`,
   * Instagram's single-media container). The composer warns — rather than
   * silently dropping — when a variant carries more than this.
   */
  publishMaxImages: number;
  maxVideos: number;
  maxAltTextChars: number;
  maxTitleChars: number | null;
  maxVideoSeconds: number | null;
  maxImageBytes: number;
  maxVideoBytes: number;
  /**
   * Fixed character cost of any URL (X t.co weighting). 0 = links counted verbatim.
   */
  urlWeight: number;
}

export interface PlatformFeatures {
  text: boolean;
  /** Links in the body are clickable / allowed. */
  link: boolean;
  images: boolean;
  video: boolean;
  /** More than one image per post (carousel / album). */
  multiImage: boolean;
  hashtags: boolean;
  mentions: boolean;
  /** Supports posting a first comment right after publishing (IG hashtags trick). */
  firstComment: boolean;
  title: boolean;
  excerpt: boolean;
  slug: boolean;
  categories: boolean;
  tags: boolean;
  altText: boolean;
  scheduling: boolean;
  /** Native multi-post threads (X). */
  threads: boolean;
  /** Cannot publish text alone — media is mandatory (Instagram). */
  imageRequired: boolean;
}

export interface PlatformAuth {
  type: 'oauth2' | 'app_password' | 'none';
  scopes: string[];
  /** Human note shown on the Connections page. */
  note: string;
}

export interface PlatformSpec {
  id: PlatformId;
  name: string;
  shortName: string;
  kind: 'social' | 'blog';
  /** UI + connectors treat these as non-publishable ("Coming soon"). */
  comingSoon: boolean;
  brandColor: string;
  /** Slightly lifted variant for dark surfaces where the brand color goes muddy. */
  brandColorDark: string;
  /** Readable foreground on top of brandColor. */
  brandForeground: string;
  hashtagStyle: 'inline' | 'trailing' | 'none';
  limits: PlatformLimits;
  aspectRatios: AspectRatio[];
  features: PlatformFeatures;
  auth: PlatformAuth;
  docsUrl: string;
  /** One-line description used on the Connections cards. */
  blurb: string;
}

const MB = 1024 * 1024;

export const PLATFORM_SPECS: Record<PlatformId, PlatformSpec> = {
  facebook: {
    id: 'facebook',
    name: 'Facebook Page',
    shortName: 'Facebook',
    kind: 'social',
    comingSoon: false,
    brandColor: '#1877F2',
    brandColorDark: '#4293FB',
    brandForeground: '#FFFFFF',
    hashtagStyle: 'trailing',
    limits: {
      maxChars: 63206,
      unlimitedChars: false,
      recommendedChars: 400,
      maxHashtags: 30,
      recommendedHashtags: 3,
      maxImages: 10,
      publishMaxImages: 1,
      maxVideos: 1,
      maxAltTextChars: 1000,
      maxTitleChars: null,
      maxVideoSeconds: 14400,
      maxImageBytes: 10 * MB,
      maxVideoBytes: 1024 * MB,
      urlWeight: 0,
    },
    aspectRatios: [
      { id: 'landscape-1.91x1', label: 'Link / landscape 1.91:1', ratio: 1.91, width: 1200, height: 630, recommended: true },
      { id: 'square', label: 'Square 1:1', ratio: 1, width: 1200, height: 1200 },
      { id: 'portrait-4x5', label: 'Portrait 4:5', ratio: 0.8, width: 1080, height: 1350 },
    ],
    features: {
      text: true,
      link: true,
      images: true,
      video: true,
      multiImage: true,
      hashtags: true,
      mentions: true,
      firstComment: false,
      title: false,
      excerpt: false,
      slug: false,
      categories: false,
      tags: false,
      altText: true,
      scheduling: true,
      threads: false,
      imageRequired: false,
    },
    auth: {
      type: 'oauth2',
      scopes: ['pages_show_list', 'pages_manage_posts', 'pages_read_engagement', 'business_management'],
      note: 'Connect with Facebook, then pick the Page you publish to.',
    },
    docsUrl: 'https://developers.facebook.com/docs/pages-api',
    blurb: 'Publish text, links and photos to a Facebook Page feed.',
  },

  instagram: {
    id: 'instagram',
    name: 'Instagram Business',
    shortName: 'Instagram',
    kind: 'social',
    comingSoon: false,
    brandColor: '#E4405F',
    brandColorDark: '#F4657F',
    brandForeground: '#FFFFFF',
    hashtagStyle: 'trailing',
    limits: {
      maxChars: 2200,
      unlimitedChars: false,
      recommendedChars: 300,
      maxHashtags: 30,
      recommendedHashtags: 8,
      maxImages: 10,
      publishMaxImages: 1,
      maxVideos: 1,
      maxAltTextChars: 100,
      maxTitleChars: null,
      maxVideoSeconds: 900,
      maxImageBytes: 8 * MB,
      maxVideoBytes: 100 * MB,
      urlWeight: 0,
    },
    aspectRatios: [
      { id: 'square', label: 'Square 1:1', ratio: 1, width: 1080, height: 1080, recommended: true },
      { id: 'portrait-4x5', label: 'Portrait 4:5', ratio: 0.8, width: 1080, height: 1350 },
      { id: 'landscape-1.91x1', label: 'Landscape 1.91:1', ratio: 1.91, width: 1080, height: 566 },
    ],
    features: {
      text: true,
      link: false,
      images: true,
      video: true,
      multiImage: true,
      hashtags: true,
      mentions: true,
      firstComment: true,
      title: false,
      excerpt: false,
      slug: false,
      categories: false,
      tags: false,
      altText: true,
      scheduling: true,
      threads: false,
      imageRequired: true,
    },
    auth: {
      type: 'oauth2',
      scopes: [
        'instagram_basic',
        'instagram_content_publish',
        'pages_show_list',
        'pages_read_engagement',
      ],
      note: 'Requires an Instagram Business or Creator account linked to a Facebook Page.',
    },
    docsUrl: 'https://developers.facebook.com/docs/instagram-api/guides/content-publishing',
    blurb: 'Publish image posts with captions, hashtags and a first comment.',
  },

  x: {
    id: 'x',
    name: 'X',
    shortName: 'X',
    kind: 'social',
    comingSoon: false,
    brandColor: '#0F1419',
    brandColorDark: '#E7E9EA',
    brandForeground: '#FFFFFF',
    hashtagStyle: 'inline',
    limits: {
      maxChars: 280,
      unlimitedChars: false,
      recommendedChars: 240,
      maxHashtags: 10,
      recommendedHashtags: 2,
      maxImages: 4,
      publishMaxImages: 1,
      maxVideos: 1,
      maxAltTextChars: 1000,
      maxTitleChars: null,
      maxVideoSeconds: 140,
      maxImageBytes: 5 * MB,
      maxVideoBytes: 512 * MB,
      urlWeight: 23,
    },
    aspectRatios: [
      { id: 'landscape-16x9', label: 'Landscape 16:9', ratio: 16 / 9, width: 1600, height: 900, recommended: true },
      { id: 'square', label: 'Square 1:1', ratio: 1, width: 1200, height: 1200 },
    ],
    features: {
      text: true,
      link: true,
      images: true,
      video: true,
      multiImage: true,
      hashtags: true,
      mentions: true,
      firstComment: false,
      title: false,
      excerpt: false,
      slug: false,
      categories: false,
      tags: false,
      altText: true,
      scheduling: true,
      threads: true,
      imageRequired: false,
    },
    auth: {
      type: 'oauth2',
      scopes: ['tweet.read', 'tweet.write', 'users.read', 'offline.access', 'media.write'],
      note: 'OAuth 2.0 with PKCE. Refresh tokens require the offline.access scope.',
    },
    docsUrl: 'https://docs.x.com/x-api/posts/creation-of-a-post',
    blurb: 'Post up to 280 characters with images, links and threads.',
  },

  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    shortName: 'LinkedIn',
    kind: 'social',
    comingSoon: false,
    brandColor: '#0A66C2',
    brandColorDark: '#3E8BDD',
    brandForeground: '#FFFFFF',
    hashtagStyle: 'trailing',
    limits: {
      maxChars: 3000,
      unlimitedChars: false,
      recommendedChars: 1200,
      maxHashtags: 30,
      recommendedHashtags: 3,
      maxImages: 9,
      publishMaxImages: 1,
      maxVideos: 1,
      maxAltTextChars: 300,
      maxTitleChars: 200,
      maxVideoSeconds: 600,
      maxImageBytes: 10 * MB,
      maxVideoBytes: 200 * MB,
      urlWeight: 0,
    },
    aspectRatios: [
      { id: 'landscape-1.91x1', label: 'Landscape 1.91:1', ratio: 1.91, width: 1200, height: 627, recommended: true },
      { id: 'square', label: 'Square 1:1', ratio: 1, width: 1200, height: 1200 },
    ],
    features: {
      text: true,
      link: true,
      images: true,
      video: true,
      multiImage: true,
      hashtags: true,
      mentions: true,
      firstComment: false,
      title: false,
      excerpt: false,
      slug: false,
      categories: false,
      tags: false,
      altText: true,
      scheduling: true,
      threads: false,
      imageRequired: false,
    },
    auth: {
      type: 'oauth2',
      scopes: ['openid', 'profile', 'email', 'w_member_social'],
      note: 'Member posting via the UGC/Posts API. Organization pages need extra review.',
    },
    docsUrl: 'https://learn.microsoft.com/en-us/linkedin/marketing/community-management/shares/posts-api',
    blurb: 'Share long-form professional updates with an image or article link.',
  },

  wordpress: {
    id: 'wordpress',
    name: 'WordPress',
    shortName: 'WordPress',
    kind: 'blog',
    comingSoon: false,
    brandColor: '#21759B',
    brandColorDark: '#3F97C0',
    brandForeground: '#FFFFFF',
    hashtagStyle: 'none',
    limits: {
      // No practical body limit — `unlimitedChars` tells the UI to hide the counter.
      maxChars: 1_000_000,
      unlimitedChars: true,
      recommendedChars: 8000,
      maxHashtags: 0,
      recommendedHashtags: 0,
      maxImages: 20,
      publishMaxImages: 1,
      maxVideos: 1,
      maxAltTextChars: 500,
      maxTitleChars: 200,
      maxVideoSeconds: null,
      maxImageBytes: 32 * MB,
      maxVideoBytes: 256 * MB,
      urlWeight: 0,
    },
    aspectRatios: [
      { id: 'featured-16x9', label: 'Featured 16:9', ratio: 16 / 9, width: 1600, height: 900, recommended: true },
      { id: 'landscape-1.91x1', label: 'Social card 1.91:1', ratio: 1.91, width: 1200, height: 630 },
    ],
    features: {
      text: true,
      link: true,
      images: true,
      video: false,
      multiImage: true,
      hashtags: false,
      mentions: false,
      firstComment: false,
      title: true,
      excerpt: true,
      slug: true,
      categories: true,
      tags: true,
      altText: true,
      scheduling: true,
      threads: false,
      imageRequired: false,
    },
    auth: {
      type: 'app_password',
      scopes: [],
      note: 'Site URL + username + Application Password (Users → Profile → Application Passwords).',
    },
    docsUrl: 'https://developer.wordpress.org/rest-api/reference/posts/',
    blurb: 'Publish long-form articles with a title, excerpt, categories and featured image.',
  },

  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    shortName: 'TikTok',
    kind: 'social',
    comingSoon: true,
    brandColor: '#FE2C55',
    brandColorDark: '#FF5B7B',
    brandForeground: '#FFFFFF',
    hashtagStyle: 'trailing',
    limits: {
      maxChars: 2200,
      unlimitedChars: false,
      recommendedChars: 150,
      maxHashtags: 20,
      recommendedHashtags: 5,
      maxImages: 35,
      publishMaxImages: 35,
      maxVideos: 1,
      maxAltTextChars: 0,
      maxTitleChars: null,
      maxVideoSeconds: 600,
      maxImageBytes: 20 * MB,
      maxVideoBytes: 500 * MB,
      urlWeight: 0,
    },
    aspectRatios: [
      { id: 'vertical-9x16', label: 'Vertical 9:16', ratio: 9 / 16, width: 1080, height: 1920, recommended: true },
    ],
    features: {
      text: false,
      link: false,
      images: true,
      video: true,
      multiImage: true,
      hashtags: true,
      mentions: true,
      firstComment: false,
      title: false,
      excerpt: false,
      slug: false,
      categories: false,
      tags: false,
      altText: false,
      scheduling: false,
      threads: false,
      imageRequired: true,
    },
    auth: {
      type: 'oauth2',
      scopes: ['video.publish', 'user.info.basic'],
      note: 'Coming soon — video publishing lands with the media pipeline.',
    },
    docsUrl: 'https://developers.tiktok.com/doc/content-posting-api-get-started',
    blurb: 'Short-form video. Coming soon.',
  },

  youtube: {
    id: 'youtube',
    name: 'YouTube',
    shortName: 'YouTube',
    kind: 'social',
    comingSoon: true,
    brandColor: '#FF0000',
    brandColorDark: '#FF4444',
    brandForeground: '#FFFFFF',
    hashtagStyle: 'trailing',
    limits: {
      maxChars: 5000,
      unlimitedChars: false,
      recommendedChars: 800,
      maxHashtags: 15,
      recommendedHashtags: 3,
      maxImages: 1,
      publishMaxImages: 1,
      maxVideos: 1,
      maxAltTextChars: 0,
      maxTitleChars: 100,
      maxVideoSeconds: 43200,
      maxImageBytes: 2 * MB,
      maxVideoBytes: 2048 * MB,
      urlWeight: 0,
    },
    aspectRatios: [
      { id: 'landscape-16x9', label: 'Landscape 16:9', ratio: 16 / 9, width: 1920, height: 1080, recommended: true },
      { id: 'vertical-9x16', label: 'Shorts 9:16', ratio: 9 / 16, width: 1080, height: 1920 },
    ],
    features: {
      text: true,
      link: true,
      images: true,
      video: true,
      multiImage: false,
      hashtags: true,
      mentions: false,
      firstComment: false,
      title: true,
      excerpt: false,
      slug: false,
      categories: true,
      tags: true,
      altText: false,
      scheduling: true,
      threads: false,
      imageRequired: true,
    },
    auth: {
      type: 'oauth2',
      scopes: ['https://www.googleapis.com/auth/youtube.upload'],
      note: 'Coming soon — video publishing lands with the media pipeline.',
    },
    docsUrl: 'https://developers.google.com/youtube/v3/docs/videos/insert',
    blurb: 'Video uploads with title, description and tags. Coming soon.',
  },
};

/** Every spec, in display order. */
export const PLATFORM_LIST: PlatformSpec[] = PLATFORM_IDS.map((id) => PLATFORM_SPECS[id]);

/** Specs that can publish today. */
export const LIVE_PLATFORM_LIST: PlatformSpec[] = PLATFORM_LIST.filter((p) => !p.comingSoon);

export function getPlatformSpec(platform: PlatformId): PlatformSpec {
  return PLATFORM_SPECS[platform];
}

export function platformName(platform: PlatformId): string {
  return PLATFORM_SPECS[platform]?.shortName ?? platform;
}

const URL_PATTERN = /https?:\/\/[^\s]+/g;

/**
 * Character count as the platform counts it. X collapses every URL to 23 chars;
 * everyone else counts verbatim. Uses code points (not UTF-16 units) so emoji
 * count as one character, matching X's own counter closely enough for the UI.
 */
export function countChars(platform: PlatformId, text: string): number {
  const spec = PLATFORM_SPECS[platform];
  const weight = spec?.limits.urlWeight ?? 0;
  if (!weight) return [...text].length;
  let total = 0;
  let lastIndex = 0;
  URL_PATTERN.lastIndex = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const index = match.index ?? 0;
    total += [...text.slice(lastIndex, index)].length + weight;
    lastIndex = index + match[0].length;
  }
  total += [...text.slice(lastIndex)].length;
  return total;
}

/** Remaining characters, or null when the platform is effectively unlimited. */
export function charsRemaining(platform: PlatformId, text: string): number | null {
  const spec = PLATFORM_SPECS[platform];
  if (!spec || spec.limits.unlimitedChars) return null;
  return spec.limits.maxChars - countChars(platform, text);
}

export function extractHashtags(text: string): string[] {
  const matches = text.match(/(^|\s)#[\p{L}\p{N}_]+/gu) ?? [];
  return matches.map((m) => m.trim());
}

export interface PlatformContentInput {
  content: string;
  mediaCount?: number;
  videoCount?: number;
  hasLink?: boolean;
  title?: string | null;
  hashtags?: string[];
  hasAccount?: boolean;
}

/**
 * Validate a draft against a platform spec. Used by the composer (live) and by
 * every connector's `validate()` before publishing.
 */
export function validatePlatformContent(
  platform: PlatformId,
  input: PlatformContentInput,
): ValidationIssue[] {
  const spec = PLATFORM_SPECS[platform];
  const issues: ValidationIssue[] = [];
  if (!spec) {
    return [{ level: 'error', code: 'not_supported', message: `Unknown platform "${platform}".` }];
  }
  if (spec.comingSoon) {
    issues.push({
      level: 'error',
      code: 'not_supported',
      message: `${spec.name} publishing is coming soon.`,
    });
  }

  const mediaCount = input.mediaCount ?? 0;
  const videoCount = input.videoCount ?? 0;
  const content = input.content ?? '';

  if (!content.trim() && !mediaCount) {
    issues.push({
      level: 'error',
      code: 'empty_content',
      message: 'Add some content before publishing.',
      field: 'content',
    });
  }

  if (!spec.limits.unlimitedChars) {
    const used = countChars(platform, content);
    if (used > spec.limits.maxChars) {
      issues.push({
        level: 'error',
        code: 'too_long',
        message: `${used - spec.limits.maxChars} characters over the ${spec.limits.maxChars.toLocaleString()} limit for ${spec.shortName}.`,
        field: 'content',
      });
    }
  }

  const hashtags = input.hashtags ?? extractHashtags(content);
  if (spec.features.hashtags && hashtags.length > spec.limits.maxHashtags) {
    issues.push({
      level: 'error',
      code: 'too_many_hashtags',
      message: `${spec.shortName} allows at most ${spec.limits.maxHashtags} hashtags.`,
      field: 'hashtags',
    });
  } else if (!spec.features.hashtags && hashtags.length > 0) {
    issues.push({
      level: 'warning',
      code: 'too_many_hashtags',
      message: `${spec.shortName} does not use hashtags — they will read as literal text.`,
      field: 'hashtags',
    });
  }

  if (mediaCount > spec.limits.maxImages) {
    issues.push({
      level: 'error',
      code: 'too_many_images',
      message: `${spec.shortName} allows at most ${spec.limits.maxImages} images.`,
      field: 'media',
    });
  } else if (mediaCount > spec.limits.publishMaxImages) {
    // The platform itself allows a carousel; our connector does not build one
    // yet and would post only the first image. Say so instead of dropping the
    // rest silently.
    const kept = spec.limits.publishMaxImages;
    issues.push({
      level: 'warning',
      code: 'extra_images_dropped',
      message:
        kept === 1
          ? `Only the first image will be published to ${spec.shortName} — multi-image posts are not supported there yet.`
          : `Only the first ${kept} images will be published to ${spec.shortName}.`,
      field: 'media',
    });
  }

  if (spec.features.imageRequired && mediaCount === 0) {
    issues.push({
      level: 'error',
      code: 'media_required',
      message: `${spec.shortName} posts require at least one image.`,
      field: 'media',
    });
  }

  if (videoCount > 0 && !spec.features.video) {
    issues.push({
      level: 'error',
      code: 'video_unsupported',
      message: `${spec.shortName} does not support video in this app yet.`,
      field: 'media',
    });
  }

  if (input.hasLink && !spec.features.link) {
    issues.push({
      level: 'warning',
      code: 'link_unsupported',
      message: `${spec.shortName} captions do not make links clickable.`,
      field: 'content',
    });
  }

  if (spec.features.title) {
    const title = (input.title ?? '').trim();
    if (!title) {
      issues.push({
        level: 'error',
        code: 'title_required',
        message: `${spec.shortName} posts need a title.`,
        field: 'title',
      });
    } else if (spec.limits.maxTitleChars && title.length > spec.limits.maxTitleChars) {
      issues.push({
        level: 'error',
        code: 'title_too_long',
        message: `Title is over the ${spec.limits.maxTitleChars} character limit.`,
        field: 'title',
      });
    }
  }

  if (input.hasAccount === false) {
    issues.push({
      level: 'error',
      code: 'account_missing',
      message: `Connect a ${spec.shortName} account first.`,
      field: 'account',
    });
  }

  return issues;
}

export function hasBlockingIssues(issues: ValidationIssue[]): boolean {
  return issues.some((issue) => issue.level === 'error');
}
